import type {CardId} from "../../cdx-models/Card";
import type {DeckId} from "../../cdx-models/Deck";
import type {MilestoneId} from "../../cdx-models/Milestone";
import type {SprintId} from "../../cdx-models/Sprint";
import type {UserId} from "../../cdx-models/User";
import {INVALIDATE} from "../mate/mate-shared";
import type {ModelName} from "../mate/mate-types";
import {cardLeavesHandsOptimistically, handUpdates} from "./hand-updates";
import {startTrackerOptimistically, stopTrackerOptimistically} from "./optimistic-trackers";
import {implCreate, implDelete, implUpdate, type ImplicitUpdate} from "./types";

export type CardsUpdateFields = {
  id?: CardId;
  deckId?: DeckId | null;
  assigneeId?: UserId | null;
  content?: string | null;
  visibility?: "default" | "archived" | "deleted";
  milestoneId?: MilestoneId | null;
  sprintId?: SprintId | null;
  status?: "not_started" | "started" | "done" | null;
  childCards?: CardId[] | null;
  addMasterTag?: string;
  removeMasterTag?: string;
  inDeps?: CardId[];
  outDeps?: CardId[];
  isDoc?: boolean;
  parentCardId?: CardId | null;
  coverFileModifier?: any;
};

type SingleCardUpdateOpts = {
  _isOptimistic?: boolean;
  _getModelFromCache: (modelName: ModelName, id: string) => any;
};

export const singleCardUpdate = (
  {
    id,
    deckId,
    assigneeId,
    content,
    visibility,
    milestoneId,
    sprintId,
    status,
    childCards,
    addMasterTag,
    removeMasterTag,
    inDeps,
    outDeps,
    isDoc,
    parentCardId,
    coverFileModifier,
  }: CardsUpdateFields,
  {_isOptimistic, _getModelFromCache}: SingleCardUpdateOpts
): ImplicitUpdate[] => {
  // card might be null!
  const card = _getModelFromCache("card", id!);
  const archiveActions = (): ImplicitUpdate[] => {
    const actions: ImplicitUpdate[] = [
      implUpdate("resolvable", {isClosed: INVALIDATE}),
      implDelete("activeTimeTracker", {cardId: id}),
      implUpdate("visionBoard", {state: INVALIDATE}),
      ...handUpdates,
    ];
    if (card && card.childCards) {
      actions.push(
        ...card.childCards.map((childId: CardId) =>
          implUpdate("card", {cardId: childId, visibility, derivedStatus: INVALIDATE})
        )
      );
    } else {
      actions.push(implUpdate("card", {visibility: INVALIDATE, derivedStatus: INVALIDATE}));
    }
    return actions;
  };

  const deleteActions = (): ImplicitUpdate[] => {
    const actions: ImplicitUpdate[] = [
      ...handUpdates,
      implUpdate("card", {
        cardId: id,
        inDeps: INVALIDATE,
        outDeps: INVALIDATE,
        hasBlockingDeps: INVALIDATE,
        isBlockingDep: INVALIDATE,
      }),
      implDelete("handCard", {cardId: id}),
      implUpdate("visionBoard", {state: INVALIDATE}),
    ];
    if (card && "parentCard" in card) {
      if (card.parentCard) {
        actions.push(
          implUpdate("card", {cardId: card.parentCard, childCards: []}),
          implUpdate("card", {cardId: id, parentCardId: null})
        );
      }
    } else {
      actions.push(
        implUpdate("card", {childCards: []}),
        implUpdate("card", {cardId: id, parentCardId: null})
      );
    }
    if (card && card.childCards) {
      actions.push(implUpdate("card", {cardId: id, childCards: []}));
    }
    return actions;
  };

  const childCardUpdateActions = (): ImplicitUpdate[] => {
    const actions: ImplicitUpdate[] = [
      implUpdate("card", {
        cardId: id,
        effort: 0,
        status: INVALIDATE,
        isDoc: INVALIDATE,
        derivedStatus: INVALIDATE,
      }),
      ...handUpdates,
    ];
    // in case a started parent got children
    if (card && (card.status === "started" || !card.status)) {
      actions.push(
        implDelete("activeTimeTracker", {cardId: id}),
        implUpdate("timeTrackingSegment", {finishedAt: INVALIDATE}),
        implDelete("timeTrackingSegment"),
        implUpdate("timeTrackingSum", {
          sumMs: INVALIDATE,
          runningModifyDurationMsBy: INVALIDATE,
          runningStartedAt: INVALIDATE,
        }),
        implDelete("timeTrackingSum")
      );
    }

    const nextChildCardSet = new Set(childCards || []);

    const prevChildren: CardId[] = (card && card.childCards) || [];
    prevChildren.forEach((cId) => {
      if (!nextChildCardSet.has(cId)) {
        // child got removed
        actions.push(implUpdate("card", {cardId: cId, parentCardId: null}));
      }
    });

    let unknownExParent = false;
    (childCards || []).forEach((childId) => {
      const child = _getModelFromCache("card", childId);
      if (!child) return;
      if ("cardParent" in child) {
        if (child.cardParent) {
          actions.push(
            implUpdate("card", {
              cardId: child.cardParent,
              childCards: INVALIDATE,
              status: INVALIDATE,
              effort: INVALIDATE,
              isDoc: INVALIDATE,
              derivedStatus: INVALIDATE,
            })
          );
        }
      } else {
        unknownExParent = true;
      }
      actions.push(implUpdate("card", {cardId: childId, parentCardId: id}));
    });
    if (unknownExParent) {
      actions.push(
        implUpdate("card", {
          childCards: INVALIDATE,
          status: INVALIDATE,
          effort: INVALIDATE,
          derivedStatus: INVALIDATE,
        })
      );
    }

    return actions;
  };

  const updates: ImplicitUpdate[] = [
    implCreate("cardHistory", {cardId: id}),
    implCreate("activity", {cardId: id, deckId, milestoneId, sprintId}),
  ];

  if (childCards) updates.push(...childCardUpdateActions());
  if (parentCardId !== undefined && card?.parentCard) {
    updates.push(implUpdate("card", {cardId: card.parentCard, childCards: INVALIDATE}));
  }
  if (sprintId !== undefined) {
    updates.push(implUpdate("card", {meta: INVALIDATE, milestoneId: INVALIDATE}));
  }
  if (deckId) {
    updates.push(
      implCreate("deckAssignment", {deckId}),
      implUpdate("deckAssignment", {deckId, lastAssignedAt: INVALIDATE}),
      implUpdate("card", {
        id,
        deckId,
        assigneeId: INVALIDATE,
        milestoneId: INVALIDATE,
        sprintId: INVALIDATE,
        derivedStatus: INVALIDATE,
      }),
      implUpdate("resolvableParticipant", {done: INVALIDATE, lastChangedAt: INVALIDATE}),
      implCreate("activity", {cardId: INVALIDATE}),
      implUpdate("cardOrder", {
        cardId: id,
        context: "deck",
        sortValue: INVALIDATE,
        label: INVALIDATE,
      })
    );
  }
  if (assigneeId) {
    updates.push(
      implCreate("assigneeAssignment", {assigneeId}),
      implUpdate("assigneeAssignment", {lastAssignedAt: INVALIDATE})
    );
  }
  if (milestoneId) {
    updates.push(
      ...handUpdates,
      implUpdate("cardOrder", {
        cardId: id,
        context: "milestone",
        sortValue: INVALIDATE,
        label: INVALIDATE,
      })
    );
  }
  if (sprintId) {
    updates.push(
      implUpdate("cardOrder", {
        cardId: id,
        context: "sprint",
        sortValue: INVALIDATE,
        label: INVALIDATE,
      })
    );
  }
  if (visibility === "deleted" || visibility === "archived") updates.push(...archiveActions());
  if (visibility === "deleted") updates.push(...deleteActions());
  if (_isOptimistic && id && (status !== undefined || visibility !== undefined)) {
    updates.push(cardLeavesHandsOptimistically({cardId: id, wasDone: card?.status === "done"}));
  }
  if (visibility === "default") {
    updates.push(...handUpdates, implUpdate("visionBoard", {state: INVALIDATE}));
  }
  if (content) {
    updates.push(implCreate("userTag"), implUpdate("card", {cardReferences: INVALIDATE}));
  }
  if (card && coverFileModifier !== undefined) {
    updates.push(implUpdate("card", {id, meta: {...card.meta, coverFileModifier}}));
  }
  if (status === "started") {
    updates.push(
      implUpdate("timeTrackingSum", {
        sumMs: INVALIDATE,
        runningModifyDurationMsBy: INVALIDATE,
        runningStartedAt: INVALIDATE,
      }),
      implDelete("timeTrackingSum"),
      ...(_isOptimistic && id ? [startTrackerOptimistically(id)] : []),
      implCreate("queueEntry"),
      implUpdate("queueEntry", {cardDoneAt: INVALIDATE, sortIndex: INVALIDATE})
    );
  }
  if (status || visibility) {
    updates.push(implUpdate("card", {hasBlockingDeps: INVALIDATE, isBlockingDep: INVALIDATE}));
  }
  if (isDoc) {
    updates.push(
      implUpdate("card", {
        cardId: id,
        effort: null,
        priority: null,
        inDeps: [],
        outDeps: [],
        hasBlockingDeps: false,
        isBlockingDep: false,
        meta: INVALIDATE,
        derivedStatus: INVALIDATE,
      })
    );
    if (card && (card.inDeps || card.outDeps)) {
      updates.push(
        implUpdate("card", {
          inDeps: INVALIDATE,
          outDeps: INVALIDATE,
          hasBlockingDeps: INVALIDATE,
          isBlockingDep: INVALIDATE,
        })
      );
    }
  }

  if (status === "started") {
    updates.push(
      implCreate("activeTimeTracker", {cardId: id}),
      implUpdate("activeTimeTracker", {cardId: id}),
      implCreate("timeTrackingSegment"),
      implUpdate("card", {id, sprintId: INVALIDATE})
    );
  }

  const couldStopTimer =
    assigneeId ||
    assigneeId === null ||
    status === "done" ||
    status === "not_started" ||
    status === null ||
    isDoc;
  if (couldStopTimer) {
    updates.push(
      ...(_isOptimistic && id ? [stopTrackerOptimistically(id)] : []),
      implDelete("activeTimeTracker", {cardId: id}),
      implUpdate("timeTrackingSegment", {finishedAt: INVALIDATE}),
      implDelete("timeTrackingSegment"),
      implUpdate("timeTrackingSum", {
        sumMs: INVALIDATE,
        runningModifyDurationMsBy: INVALIDATE,
        runningStartedAt: INVALIDATE,
      }),
      implDelete("timeTrackingSum"),
      // also affect queue entries
      implCreate("queueEntry"),
      implUpdate("queueEntry", {cardDoneAt: INVALIDATE, sortIndex: INVALIDATE})
    );
  }

  if (addMasterTag || removeMasterTag) {
    updates.push(implUpdate("card", {id, masterTags: INVALIDATE}));
  }
  if (card?.parentCard) {
    updates.push(
      implUpdate("card", {
        id: card?.parentCard,
        status: INVALIDATE,
        isDoc: INVALIDATE,
        derivedStatus: INVALIDATE,
      })
    );
  }
  if (inDeps || outDeps) {
    updates.push(
      implUpdate("card", {
        inDeps: INVALIDATE,
        outDeps: INVALIDATE,
        hasBlockingDeps: INVALIDATE,
        isBlockingDep: INVALIDATE,
      })
    );
  }
  if (
    status ||
    assigneeId !== undefined ||
    visibility ||
    isDoc !== undefined ||
    childCards !== undefined ||
    deckId !== undefined
  ) {
    updates.push(implUpdate("card", {id, derivedStatus: INVALIDATE}));
  }

  return updates;
};
